import { Button, Col, Form, message, Row, Space } from 'antd'
import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { ReactFacebookFailureResponse, ReactFacebookLoginInfo } from 'react-facebook-login'
import { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login'
import { useHistory, useLocation } from 'react-router'
import { LoginDrawer } from '../../components/LoginDrawer/LoginDrawer'
import { popMobileDrawer } from '../../components/LoginDrawer/MobileDrawer'
import {
  popRetryableLoginVerifyOtp,
  popRetryableRegisterVerifyOtp,
} from '../../components/LoginDrawer/OTPDrawer'
import { RegisterDrawer } from '../../components/LoginDrawer/RegisterDrawer'
import CarRentalLoginVariant from '../../components/LoginDrawer/CarRentalLoginVariant'
import { HaupWrapper } from '../../components/MobileWrapper'
import config from '../../config'
import { login } from '../../contexts/Auth'
import { useLoading } from '../../contexts/LoadingContext'
import { appleLogin } from '../../services/auth/apple.service'
import {
  instanceOfFacebookLoginFailure,
  instanceOfFacebookLoginResponse,
} from '../../services/auth/facebook-auth.function'
import { facebookLogin } from '../../services/auth/facebook-auth.service'
import {
  instanceOfGoogleLoginResponse,
  instanceOfGoogleLoginResponseOffline,
} from '../../services/auth/google-auth.function'
import { googleLogin } from '../../services/auth/google-auth.service'
import {
  instanceOfGetOTPSuccess,
  instanceOfLoginSuccess,
  instanceOfMobileDuplicated,
  instanceOfMobileRequiredToRegister,
  instanceOfRequireMobile,
  instanceOfRequireOTP,
} from '../../services/auth/instanceof.function'
import { lineLogin } from '../../services/auth/line.service'
import { userLoginGetMobileOTP } from '../../services/auth/login.service'
import { userRegisterGetMobileOtp } from '../../services/auth/register.service'
import MixpanelTracker from '../../services/track/mixpanel'
import { AFFILIATE_CODE_QUERY_PARAM, buildShareCarPath } from '../../utils/affiliate-code'
import { getStoredPageLang, PageLang, setStoredPageLang } from './pageLang'
import CarEstimateModal from './sections/CarEstimateModal'
import ChoosePlanSection from './sections/ChoosePlanSection'
import ComparePlansModal from './sections/ComparePlansModal'
import ContactFooterSection from './sections/ContactFooterSection'
import EarningPotentialSection from './sections/EarningPotentialSection'
import HeroSection from './sections/HeroSection'
import SocialSection from './sections/SocialSection'
import TestimonialsSection from './sections/TestimonialsSection'
import { showApiErrorModal } from '../../utils/apiErrorModal'

const ctaContent: Record<
  PageLang,
  { heading: string; subtext: string; lineButton: string; divider: string; loginLink: string }
> = {
  EN: {
    heading: 'Start your HAUP listing in just 3 steps',
    subtext: 'Login or create your account to get started.',
    lineButton: 'Log in or register with LINE',
    divider: 'Or log in with email or others',
    loginLink: 'Login',
  },
  TH: {
    heading: 'เริ่มลงทะเบียนรถกับ HAUP ใน 3 ขั้นตอน',
    subtext: 'เข้าสู่ระบบหรือสร้างบัญชีเพื่อเริ่มต้นใช้งาน',
    lineButton: 'เข้าสู่ระบบหรือลงทะเบียนด้วย LINE',
    divider: 'หรือเข้าสู่ระบบด้วยอีเมลหรือช่องทางอื่น',
    loginLink: 'เข้าสู่ระบบ',
  },
}

export const LoginPage = (props: { socialLogin?: any; channel?: string }) => {
  const [panelOpen, setPanelOpen] = useState<'login' | 'register' | ''>('')
  const [showComparePlansModal, setShowComparePlansModal] = useState(false)
  const [showCarEstimateModal, setShowCarEstimateModal] = useState(false)
  const [pageLang, setPageLang] = useState<PageLang>(getStoredPageLang)
  const handleChangeLang = (lang: PageLang) => {
    MixpanelTracker.track('Registration Page Language Change Click', { lang })
    setPageLang(lang)
    setStoredPageLang(lang)
  }
  const [loginFormRef] = Form.useForm()
  const [registerFormRef] = Form.useForm()
  const { GOOGLE_APP_ID, FACEBOOK_APP_ID } = config.social
  const mobileDrawerRef = useRef<HTMLDivElement>(null)
  const otpDrawerRef = useRef<HTMLDivElement>(null)
  const history = useHistory()
  const location = useLocation()
  const { setLoading } = useLoading()

  const queryParams = new URLSearchParams(location.search)
  const requestId = queryParams.get('request_id')
  const affiliateCode = queryParams.get(AFFILIATE_CODE_QUERY_PARAM)
  const isCarRentalVariant = Boolean(requestId)

  const handleLineLoginClick = () => {
    MixpanelTracker.track('Registration Page Line Login Click')
    const state = Math.random().toString(36).substring(2, 15)
    const nonce = Math.random().toString(36).substring(2, 15) + Date.now()
    const lineAuthUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${
      config.social.LINE_LOGIN_ID
    }&redirect_uri=${encodeURIComponent(
      config.social.LINE_CALLBACK_URL,
    )}&state=${state}&scope=profile openid email&nonce=${nonce}&bot_prompt=normal`
    window.location.href = lineAuthUrl
  }

  const handleOtherLoginClick = () => {
    MixpanelTracker.track('Registration Page Login With Others Click')
    setPanelOpen((prev) => (!prev ? 'login' : ''))
  }

  const scrollToEarningPotential = () => {
    MixpanelTracker.track('Registration Page See Earnings Click')
    document.getElementById('earning-potential')?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      const userToken = localStorage.getItem('userToken')
      if (userToken) {
        const postLoginRedirect = localStorage.getItem('postLoginRedirect')
        if (postLoginRedirect) {
          localStorage.removeItem('postLoginRedirect')
          history.replace(postLoginRedirect)
        } else if (requestId) {
          history.replace(buildShareCarPath(requestId, affiliateCode))
        } else {
          history.replace('/submission')
        }
      }
    }

    checkAuthAndRedirect()
  }, [])

  useEffect(() => {
    MixpanelTracker.track('Page View', {
      'Page Name': 'Registration page',
    })

    if (requestId) {
      localStorage.setItem('postLoginRedirect', buildShareCarPath(requestId, affiliateCode))
    }
  }, [requestId])

  function redirectToBase() {
    const postLoginRedirect = localStorage.getItem('postLoginRedirect')
    if (postLoginRedirect) {
      localStorage.removeItem('postLoginRedirect')
      history.replace(postLoginRedirect)
      return
    }

    const locationState = location.state as { from?: string }

    const queryParams = new URLSearchParams(location.search)
    const requestId = queryParams.get('request_id')

    if (requestId) {
      history.replace(buildShareCarPath(requestId, queryParams.get(AFFILIATE_CODE_QUERY_PARAM)))
      return
    }

    const redirectPath = locationState?.from || '/'
    history.replace(redirectPath)
  }

  const loginWithTracking = async (
    accessToken: string,
    userData: any,
    redirectFn: () => void,
    source: string,
    isNewUser = false,
  ) => {
    MixpanelTracker.track(isNewUser ? 'Register Success' : 'Login Success', {
      source: source,
      'User ID': userData.userid,
      Email: userData.username,
      'Registration Method': source,
      'User Name': userData.name + userData.lastname,
      'Login Type': isNewUser ? 'New User' : 'Returning User',
      Timestamp: new Date().toISOString(),
    })

    MixpanelTracker.identify(userData.userid)

    return await login(accessToken, userData, redirectFn)
  }

  useEffect(() => {
    if (props?.socialLogin) {
      handleSocialLoginSuccess(props?.socialLogin)
    }
  }, [])

  async function handleSocialLoginSuccess(res: any): Promise<void> {
    if (!otpDrawerRef.current || !mobileDrawerRef.current) {
      message.error('No window element provided')
      throw 'No window element provided'
    }
    const loginResponse = res
    const socialSource =
      props?.channel === 'APPLE' ? 'Apple ID' : props?.channel === 'LINE' ? 'LINE' : 'Google'

    if (instanceOfLoginSuccess(loginResponse)) {
      return await loginWithTracking(
        loginResponse.accessToken,
        loginResponse.userData,
        redirectToBase,
        socialSource,
        loginResponse.isNewUser ?? false,
      )
    }
    if (instanceOfRequireMobile(loginResponse)) {
      const personalInfo = await popMobileDrawer(
        mobileDrawerRef.current,
        loginResponse.firstName,
        loginResponse.lastName,
        loginResponse.email,
        undefined,
        undefined,
        loginResponse.photoUrl,
      )
      if (!personalInfo) {
        return
      }
      setLoading(true)
      const requestOTP = await userLoginGetMobileOTP(
        Number(personalInfo.mobilePrefix),
        personalInfo.mobile,
        loginResponse.loginToken,
        personalInfo.email,
        personalInfo.firstName,
        personalInfo.lastName,
        personalInfo.photoUrl || loginResponse.photoUrl,
      ).finally(() => setLoading(false))
      if (instanceOfMobileDuplicated(requestOTP)) {
        return showApiErrorModal(requestOTP)
      }
      if (instanceOfRequireMobile(requestOTP)) {
        throw 'Impossible return object. Something went wrong ?'
      }
      if (instanceOfGetOTPSuccess(requestOTP)) {
        const loginSuccess = await popRetryableLoginVerifyOtp(otpDrawerRef.current, {
          mobile: requestOTP.mobile,
          refCode: requestOTP.refCode,
        }).catch((error: any) => {
          message.error(error)
          throw error
        })
        if (!(loginSuccess.accessToken && loginSuccess.userData)) {
          throw 'Invalid Login Data'
        }
        return loginWithTracking(
          loginSuccess.accessToken,
          loginSuccess.userData,
          redirectToBase,
          socialSource,
        )
      }
    }
    if (instanceOfRequireOTP(loginResponse)) {
      const mobileNumber = loginResponse.otp.mobile
      const refCode = loginResponse.otp.refCode

      const loginSuccess = await popRetryableLoginVerifyOtp(otpDrawerRef.current, {
        mobile: mobileNumber,
        refCode: refCode,
      }).catch((error: any) => {
        message.error(error)
        throw error
      })

      if (!(loginSuccess.accessToken && loginSuccess.userData)) {
        throw 'Invalid Login Data'
      }
      return loginWithTracking(
        loginSuccess.accessToken,
        loginSuccess.userData,
        redirectToBase,
        socialSource,
      )
    }
    if (instanceOfMobileRequiredToRegister(loginResponse)) {
      const personalInfo = await popMobileDrawer(
        mobileDrawerRef.current,
        loginResponse.firstName,
        loginResponse.lastName,
        loginResponse.email,
        undefined,
        undefined,
        loginResponse.photoUrl,
        loginResponse.token,
      )
      if (!personalInfo) {
        return
      }

      const mobileCountryCode = Number(personalInfo.mobilePrefix)

      if (mobileCountryCode !== 66) {
        setLoading(true)
        try {
          if (props?.channel === 'LINE') {
            const lineLoginPayload = {
              token: loginResponse.token || '',
              email: personalInfo.email,
              firstName: personalInfo.firstName,
              lastName: personalInfo.lastName,
              mobileNo: personalInfo.mobile,
              mobileCountryCode: mobileCountryCode,
              project: 'vehicle-submission',
              deviceType: 'WEBSITE',
              callBackUrl: config.social.LINE_CALLBACK_URL,
            }
            const finalLoginResponse = await axios.post(
              `${config.authService.URL}/social-login-v2/line`,
              lineLoginPayload,
              {
                validateStatus(status) {
                  return [419, 400, 303].includes(status) || (status >= 200 && status < 300)
                },
              },
            )
            if (finalLoginResponse.data.accessToken && finalLoginResponse.data.userData) {
              return loginWithTracking(
                finalLoginResponse.data.accessToken,
                finalLoginResponse.data.userData,
                redirectToBase,
                socialSource,
                true,
              )
            } else {
              throw new Error(finalLoginResponse.data.message || 'Registration failed')
            }
          }
        } catch (error: any) {
          message.error(error.message || 'Registration failed')
          throw error
        } finally {
          setLoading(false)
        }
        return
      }

      setLoading(true)
      const requestOTP = await userRegisterGetMobileOtp(
        mobileCountryCode,
        personalInfo.mobile,
        personalInfo.email,
        personalInfo.firstName,
        personalInfo.lastName,
        personalInfo.photoUrl || loginResponse.photoUrl,
      ).finally(() => setLoading(false))
      if (instanceOfMobileDuplicated(requestOTP)) {
        return showApiErrorModal(requestOTP)
      }
      const registerOtpValid = await popRetryableRegisterVerifyOtp(otpDrawerRef.current, {
        mobile: requestOTP.mobile,
        refCode: requestOTP.refCode,
      }).catch((error: any) => {
        message.error(error)
        throw error
      })
      const finalLoginResponse =
        props?.channel === 'APPLE'
          ? await appleLogin(res.tokenId, registerOtpValid.token)
          : props?.channel === 'LINE'
          ? await lineLogin(loginResponse.token || '', registerOtpValid.token, personalInfo.email)
          : await googleLogin(res.tokenId, registerOtpValid.token)
      if (instanceOfRequireMobile(finalLoginResponse)) {
        return message.error(finalLoginResponse.message)
      }
      if (instanceOfRequireOTP(finalLoginResponse)) {
        return message.error(finalLoginResponse.message)
      }
      return loginWithTracking(
        finalLoginResponse.accessToken,
        finalLoginResponse.userData,
        redirectToBase,
        socialSource,
        true,
      )
    }
  }

  async function handleGoogleLoginSuccess(
    res: GoogleLoginResponse | GoogleLoginResponseOffline,
  ): Promise<void> {
    if (!otpDrawerRef.current || !mobileDrawerRef.current) {
      message.error('No window element provided')
      throw 'No window element provided'
    }
    if (instanceOfGoogleLoginResponseOffline(res)) {
      return message.error(res.code)
    }
    if (instanceOfGoogleLoginResponse(res)) {
      const loginResponse = await googleLogin(res.tokenId)
      if (instanceOfLoginSuccess(loginResponse)) {
        return await loginWithTracking(
          loginResponse.accessToken,
          loginResponse.userData,
          redirectToBase,
          'Google',
          loginResponse.isNewUser ?? false,
        )
      }
      if (instanceOfRequireMobile(loginResponse)) {
        const mobileRequiredResponse = loginResponse as any
        const personalInfo = await popMobileDrawer(
          mobileDrawerRef.current,
          mobileRequiredResponse.firstName || res.profileObj?.givenName,
          mobileRequiredResponse.lastName || res.profileObj?.familyName,
          mobileRequiredResponse.email || res.profileObj?.email,
          undefined,
          undefined,
          mobileRequiredResponse.photoUrl || res.profileObj?.imageUrl,
        )
        if (!personalInfo) {
          return
        }
        setLoading(true)
        const requestOTP = await userLoginGetMobileOTP(
          Number(personalInfo.mobilePrefix),
          personalInfo.mobile,
          loginResponse.loginToken,
          personalInfo.email,
          personalInfo.firstName,
          personalInfo.lastName,
          personalInfo.photoUrl,
        ).finally(() => setLoading(false))
        if (instanceOfMobileDuplicated(requestOTP)) {
          return showApiErrorModal(requestOTP)
        }
        if (instanceOfRequireMobile(requestOTP)) {
          throw 'Impossible return object. Something went wrong ?'
        }
        if (instanceOfGetOTPSuccess(requestOTP)) {
          const loginSuccess = await popRetryableLoginVerifyOtp(otpDrawerRef.current, {
            mobile: requestOTP.mobile,
            refCode: requestOTP.refCode,
          }).catch((error: any) => {
            message.error(error)
            throw error
          })
          if (!(loginSuccess.accessToken && loginSuccess.userData)) {
            throw 'Invalid Login Data'
          }
          return loginWithTracking(
            loginSuccess.accessToken,
            loginSuccess.userData,
            redirectToBase,
            'Google',
          )
        }
      }
      if (instanceOfRequireOTP(loginResponse)) {
        const mobileNumber = loginResponse.otp.mobile
        const refCode = loginResponse.otp.refCode

        const loginSuccess = await popRetryableLoginVerifyOtp(otpDrawerRef.current, {
          mobile: mobileNumber,
          refCode: refCode,
        }).catch((error: any) => {
          message.error(error)
          throw error
        })

        if (!(loginSuccess.accessToken && loginSuccess.userData)) {
          throw 'Invalid Login Data'
        }
        return loginWithTracking(
          loginSuccess.accessToken,
          loginSuccess.userData,
          redirectToBase,
          'Google',
        )
      }
      if (instanceOfMobileRequiredToRegister(loginResponse)) {
        const registerRequiredResponse = loginResponse as any
        const personalInfo = await popMobileDrawer(
          mobileDrawerRef.current,
          registerRequiredResponse.firstName || res.profileObj?.givenName,
          registerRequiredResponse.lastName || res.profileObj?.familyName,
          registerRequiredResponse.email || res.profileObj?.email,
          undefined,
          undefined,
          registerRequiredResponse.photoUrl || res.profileObj?.imageUrl,
          registerRequiredResponse.token,
        )
        if (!personalInfo) {
          return
        }
        setLoading(true)
        const requestOTP = await userRegisterGetMobileOtp(
          Number(personalInfo.mobilePrefix),
          personalInfo.mobile,
          personalInfo.email,
          personalInfo.firstName,
          personalInfo.lastName,
          personalInfo.photoUrl,
        ).finally(() => setLoading(false))
        if (instanceOfMobileDuplicated(requestOTP)) {
          return showApiErrorModal(requestOTP)
        }
        const registerOtpValid = await popRetryableRegisterVerifyOtp(otpDrawerRef.current, {
          mobile: requestOTP.mobile,
          refCode: requestOTP.refCode,
        }).catch((error: any) => {
          message.error(error)
          throw error
        })
        const finalLoginResponse = await googleLogin(res.tokenId, registerOtpValid.token)
        if (instanceOfRequireMobile(finalLoginResponse)) {
          return message.error(finalLoginResponse.message)
        }
        if (instanceOfRequireOTP(finalLoginResponse)) {
          return message.error(finalLoginResponse.message)
        }
        return loginWithTracking(
          finalLoginResponse.accessToken,
          finalLoginResponse.userData,
          redirectToBase,
          'Google',
          true,
        )
      }
    }
  }

  async function handleFacebookLogin(res: ReactFacebookLoginInfo | ReactFacebookFailureResponse) {
    if (!otpDrawerRef.current || !mobileDrawerRef.current) {
      message.error('No window element provided')
      throw 'No window element provided'
    }
    if (instanceOfFacebookLoginFailure(res)) {
      return message.error(res.status)
    }
    if (instanceOfFacebookLoginResponse(res)) {
      const loginResult = await facebookLogin(res.accessToken)
      if (instanceOfLoginSuccess(loginResult)) {
        return await loginWithTracking(
          loginResult.accessToken,
          loginResult.userData,
          redirectToBase,
          'Facebook',
          loginResult.isNewUser ?? false,
        )
      }

      if (instanceOfRequireMobile(loginResult)) {
        const personalInfo = await popMobileDrawer(
          mobileDrawerRef.current,
          loginResult.firstName,
          loginResult.lastName,
          loginResult.email,
          undefined,
          undefined,
          loginResult.photoUrl,
        )
        if (!personalInfo) {
          return
        }
        setLoading(true)
        const requestOTP = await userLoginGetMobileOTP(
          Number(personalInfo.mobilePrefix),
          personalInfo.mobile,
          loginResult.loginToken,
          personalInfo.email,
          personalInfo.firstName,
          personalInfo.lastName,
          personalInfo.photoUrl,
        ).finally(() => setLoading(false))
        if (instanceOfMobileDuplicated(requestOTP)) {
          return showApiErrorModal(requestOTP)
        }
        if (instanceOfRequireMobile(requestOTP)) {
          throw 'Impossible return object. Something went wrong ?'
        }
        if (instanceOfGetOTPSuccess(requestOTP)) {
          const loginSuccess = await popRetryableLoginVerifyOtp(otpDrawerRef.current, {
            mobile: requestOTP.mobile,
            refCode: requestOTP.refCode,
          }).catch((error: any) => {
            message.error(error)
            throw error
          })

          if (!(loginSuccess.accessToken && loginSuccess.userData)) {
            throw 'Invalid Login Data'
          }
          return loginWithTracking(
            loginSuccess.accessToken,
            loginSuccess.userData,
            redirectToBase,
            'Facebook',
          )
        }
      }

      if (instanceOfRequireOTP(loginResult)) {
        const mobileNumber = loginResult.otp.mobile
        const refCode = loginResult.otp.refCode

        const loginSuccess = await popRetryableLoginVerifyOtp(otpDrawerRef.current, {
          mobile: mobileNumber,
          refCode: refCode,
        }).catch((error: any) => {
          message.error(error)
          throw error
        })

        if (!(loginSuccess.accessToken && loginSuccess.userData)) {
          throw 'Invalid Login Data'
        }
        return loginWithTracking(
          loginSuccess.accessToken,
          loginSuccess.userData,
          redirectToBase,
          'Facebook',
        )
      }
    }
  }

  if (isCarRentalVariant) {
    return (
      <HaupWrapper>
        <Row justify="center" style={{ height: '100%' }}>
          <Col
            flex="0 1 500px"
            style={{
              backgroundColor: '#fff',
              position: 'relative',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
            }}
          >
            <CarRentalLoginVariant
              carIconUrl="https://ts-upload.sgp1.digitaloceanspaces.com/cms/1trjokeLyxLJxWrDC2GFmDrVPojbah43M459XKsoWTctgfCABSzoYfhT4ncr9yQ5cxHNKq1163PkwHbqZyvQs6qn.png"
              requestId={requestId || ''}
              onLoginClick={() => {
                MixpanelTracker.track('Registration Page Car Rental Login Click')
                setPanelOpen('login')
              }}
            />
          </Col>
        </Row>

        <LoginDrawer
          formRef={loginFormRef}
          open={panelOpen === 'login'}
          onClose={(open) =>
            open ? setPanelOpen(open) : setPanelOpen((prev) => (!prev ? 'login' : ''))
          }
        />
        <RegisterDrawer
          formRef={registerFormRef}
          open={panelOpen === 'register'}
          onClose={(open) =>
            open ? setPanelOpen(open) : setPanelOpen((prev) => (!prev ? 'register' : ''))
          }
          onGoogleLoginSuccess={handleGoogleLoginSuccess}
          onFacebookLoginSuccess={handleFacebookLogin}
        />
        <div ref={mobileDrawerRef} />
        <div ref={otpDrawerRef} />
      </HaupWrapper>
    )
  }

  return (
    <HaupWrapper>
      <Row justify="center" style={{ height: '100%' }}>
        <Col
          flex="0 1 490px"
          style={{
            backgroundColor: '#F8FBFF',
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          }}
        >
          <div style={{ fontFamily: 'Poppins, sans-serif' }}>
            <HeroSection
              lang={pageLang}
              onChangeLang={handleChangeLang}
              onLineLogin={handleLineLoginClick}
              onOtherLogin={handleOtherLoginClick}
            />
            <EarningPotentialSection
              lang={pageLang}
              onOpenCarEstimateModal={() => {
                MixpanelTracker.track('Registration Page Car Estimate Click')
                setShowCarEstimateModal(true)
              }}
            />
            <ChoosePlanSection
              lang={pageLang}
              onSeeEarnings={scrollToEarningPotential}
              onComparePlans={() => {
                MixpanelTracker.track('Registration Page Compare Plans Click')
                setShowComparePlansModal(true)
              }}
            />
            <TestimonialsSection lang={pageLang} />
            <SocialSection lang={pageLang} />

            <div
              style={{
                margin: '32px 16px',
                borderRadius: '20px',
                padding: '32px 24px',
                background: 'linear-gradient(155deg, #0058D6 0%, #0048B3 45%, #00206B 100%)',
              }}
            >
              <h2 style={{ fontSize: '22px', fontWeight: 600, color: '#fff', margin: '0 0 6px' }}>
                {ctaContent[pageLang].heading}
              </h2>
              <p style={{ fontSize: '14px', color: '#DCE7FA', marginBottom: '24px' }}>
                {ctaContent[pageLang].subtext}
              </p>

              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Button
                  block
                  shape="round"
                  size="large"
                  onClick={handleLineLoginClick}
                  style={{
                    height: '60px',
                    backgroundColor: '#06C755',
                    borderColor: '#06C755',
                    borderWidth: '2px',
                    borderRadius: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#FFFFFF',
                    boxShadow: '0 4px 12px rgba(6, 199, 85, 0.25)',
                  }}
                >
                  <img
                    src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/2bkc38bGtiA2rbdewHrR2GHa9xbQwoBg6VDWPAFxyUUpFVxVTy8UHWjt96chwgs5AM1sZ29qG6u8aAeVo4DxUu9z.png"
                    alt="LINE"
                    style={{ width: '32px', height: '32px' }}
                  />
                  {ctaContent[pageLang].lineButton}
                </Button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.4)' }}
                  />
                  <span style={{ fontSize: '13px', color: '#fff', whiteSpace: 'nowrap' }}>
                    {ctaContent[pageLang].divider}
                  </span>
                  <div
                    style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.4)' }}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <a
                    onClick={handleOtherLoginClick}
                    style={{
                      color: '#fff',
                      fontSize: '16px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    {ctaContent[pageLang].loginLink}
                  </a>
                </div>
              </Space>
            </div>

            <ContactFooterSection lang={pageLang} />
          </div>
        </Col>
      </Row>

      <ComparePlansModal
        lang={pageLang}
        open={showComparePlansModal}
        onClose={() => setShowComparePlansModal(false)}
      />

      <CarEstimateModal
        lang={pageLang}
        open={showCarEstimateModal}
        onClose={() => setShowCarEstimateModal(false)}
      />

      <LoginDrawer
        formRef={loginFormRef}
        open={panelOpen === 'login'}
        onClose={(open) =>
          open ? setPanelOpen(open) : setPanelOpen((prev) => (!prev ? 'login' : ''))
        }
      />
      <RegisterDrawer
        formRef={registerFormRef}
        open={panelOpen === 'register'}
        onClose={(open) =>
          open ? setPanelOpen(open) : setPanelOpen((prev) => (!prev ? 'register' : ''))
        }
        onGoogleLoginSuccess={handleGoogleLoginSuccess}
        onFacebookLoginSuccess={handleFacebookLogin}
      />
      <div ref={mobileDrawerRef} />
      <div ref={otpDrawerRef} />
    </HaupWrapper>
  )
}

export default LoginPage
